import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';
import RHFTextField from '#component/form/rhf/rhf-text-field';
import SelectProject from '#component/form/multiple-select-project';
import { validDataForSelect } from '#component/warehouse/appliance-form/form/helper';
import { useOptionsService } from '#service/autocomplete-service/hooks';
import { WarehouseFilterOptionService } from '#service/autocomplete-service/service';
import {
  Container,
  StyledPaper,
  ButtonContainer,
  StyledButton
} from './styles';
import { Option, SingleValue } from './select-components';
import { useDebounce } from '#hooks';

const components = {
  Option,
  SingleValue
};

/**
 * Renders appliance form.
 * @returns {React.FC}
 */

const ApplianceForm = props => {
  const location = useLocation();

  const {
    isDirty,
    onSubmit,
    nextStep,
    applianceId,
    history,
    title,
    forceError,
    isApplianceStop
  } = props;

  const { control, setValue, watch, formState: { isSubmitting } } = useFormContext();
  const formAllData = watch();

  const { options, status, ...serviceMethods } =
    useOptionsService(['label'], WarehouseFilterOptionService);

  const textFieldOnFocus = () => {
    serviceMethods.fetchOptions();
  };

  const handleSitesFetch = useDebounce(v => {
    serviceMethods.fetchOptions(v);
  }, 500);

  const handleClear = () => {
    serviceMethods.clearOptions();
  };

  const handleCancelClick = () => {
    if (location.key) {
      history.goBack();
    } else {
      history.push('/warehouse');
    }
  };

  const handleSiteSetData = d => {
    setValue('warehouse', d, { shouldDirty: true, shouldValidate: true });
  };

  const handleChange = event => {
    const { value: newValue } = event.target;

    if (newValue.length > 1) {
      handleSitesFetch(newValue);
    }
  };

  const warehousesToSelect = validDataForSelect(options);

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        {title}
      </Typography>
      <form onSubmit={onSubmit} autoComplete="off">
        <StyledPaper elevation={0}>
          <RHFTextField
            name="label"
            label="Application name"
            variant="outlined"
            fullWidth
            margin="normal"
            forceError={forceError}
            disabled={isApplianceStop}
          />
          <Controller
            name="warehouse"
            control={control}
            render={({ field: { onChange }, fieldState: { error } }) => (
              <SelectProject
                label="Site"
                placeholder="Site"
                margin="normal"
                variant="outlined"
                data={warehousesToSelect}
                setData={p => {
                  onChange(p);
                  handleSiteSetData(p);
                }}
                isMulti={false}
                components={components}
                showProjectName={true}
                defaultData={formAllData?.warehouse}
                forceError={forceError}
                disabled={isApplianceStop}
                fetchProjects={handleChange}
                clearProjects={handleClear}
                loading={['loading'].includes(status)}
                textFieldOnFocus={textFieldOnFocus}
                meta={{
                  error: error?.message,
                  valid: !error,
                  invalid: !!error,
                  submitFailed: true
                }}
              />
            )}
          />
        </StyledPaper>
        <ButtonContainer>
          <StyledButton
            variant="outlined"
            onClick={handleCancelClick}
          >
            Cancel
          </StyledButton>
          <StyledButton
            disabled={isSubmitting}
            onClick={nextStep}
            variant="outlined"
          >
            Next
          </StyledButton>
          {applianceId && (
            <StyledButton
              disabled={isSubmitting || !isDirty || isApplianceStop}
              type="submit"
              color="secondary"
              variant="contained"
            >
              Save
            </StyledButton>
          )}
        </ButtonContainer>
      </form>
    </Container>
  );
};

export default ApplianceForm;
